import { isSameDay, isSameMonth, isSameWeek } from "date-fns";

import { parseApiDateWithUtcTz } from "@app/util/parsers";

import { DailyGranularity } from "./getDistanceBuckets";

export type DistanceMetric = "avg" | "sum";

// The aggregation layer aliases the date key to the interval name, and one metric per request means
// only the requested one comes back, so every key here is optional by shape.
export interface DistanceAggBucket {
  avg?: number;
  day?: string;
  month?: string;
  sum?: number;
  week?: string;
}

// Monday-based to match PG DATE_TRUNC('week'), which cannot be offset for this column. A Sunday-
// start org therefore has one day in the neighbouring bucket; the user's week start would double it.
const isSameBucket: Record<DailyGranularity, (a: Date, b: Date) => boolean> = {
  day: isSameDay,
  month: isSameMonth,
  week: (a, b) => isSameWeek(a, b, { weekStartsOn: 1 }),
};

// Date-only keys are normalised too: parseApiDateWithUtcTz skips parseApiDate's length-10 handling.
const parseBucketKey = (key: string) =>
  parseApiDateWithUtcTz(key.length === 10 ? `${key}T00:00:00Z` : key);

// The server returns only periods that had rows, so the chart's empty buckets are filled here.
export const fillDistanceBuckets = (
  aggregations: DistanceAggBucket[],
  buckets: Date[],
  granularity: DailyGranularity,
  metric: DistanceMetric
): { date: Date; distance: number }[] => {
  const matches = isSameBucket[granularity];
  // Read as UTC so the zone-less calendar date lands on the local midnight the buckets use.
  const parsed = aggregations.map((bucket) => ({
    date: parseBucketKey(bucket[granularity] ?? ""),
    distance: Math.round(bucket[metric] ?? 0),
  }));

  return buckets.map((date) => ({
    date,
    distance:
      parsed.find(({ date: bucketDate }) => !Number.isNaN(+bucketDate) && matches(bucketDate, date))
        ?.distance ?? 0,
  }));
};

export default fillDistanceBuckets;
